import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MdExpandLess, MdExpandMore, MdViewColumn, MdVisibilityOff } from 'react-icons/md';
import { RxCaretDown, RxCaretSort } from 'react-icons/rx';

const ResponderBaseTable = ({
  rows = [],
  columns = [],
  dropdownMenu = () => <div>No Menu</div>,
  onRowClick,
  selectedRowId = null,
  selectedRowIds = [],
  selectedRowColor = 'var(--color-primary-highlight)',
  selectedRowTextColor = 'var(--color-primary-highlight-foreground)',
  expandable = false,
  altRowSettings = {},
  customStyles = {},
  muiTableContainerProps = {},
  muiTablePaperProps = {},
  enableRowVirtualization = true,
  enableColumnActions = false,
  initialState = {},
  customEmptyMessage = 'No records to display',
}) => {
  return (
    <MaterialReactTable
      enableColumnFilters={false}
      initialState={{
        showColumnFilters: false,
        ...initialState,
      }}
      columnFilterDisplayMode="popover"
      enableColumnActions={enableColumnActions}
      enableHiding={true}
      renderColumnActionsMenuItems={({ column, table }) => [
        <MenuItem key="hide" onClick={() => column.toggleVisibility(false)} sx={{ m: 0 }}>
          <ListItemIcon>
            <MdVisibilityOff />
          </ListItemIcon>
          <ListItemText>Hide</ListItemText>
        </MenuItem>,
        <MenuItem
          key="show-all"
          onClick={() => {
            table.getAllColumns().forEach((col) => {
              if (col.getCanHide()) {
                col.toggleVisibility(true);
              }
            });
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <MdViewColumn />
          </ListItemIcon>
          <ListItemText>Show All Columns</ListItemText>
        </MenuItem>,
      ]}
      icons={{
        SortIcon: () => <RxCaretSort />,
        ArrowDownwardIcon: () => <RxCaretDown />,
      }}
      columns={columns}
      data={rows}
      enableBottomToolbar={false}
      enableTopToolbar={false}
      enableExpanding={expandable}
      enableExpandAll={false}
      enableRowVirtualization={enableRowVirtualization}
      enablePagination={false}
      rowVirtualizerOptions={{ estimateSize: () => 70 }}
      muiExpandButtonProps={({ row }) => ({
        children: row.getIsExpanded() ? <MdExpandLess /> : <MdExpandMore />,
        sx: { display: 'none' },
      })}
      muiTablePaperProps={{
        ...muiTablePaperProps,
        sx: {
          width: '100%',
          backgroundColor: 'var(--color-base)',
          color: 'var(--color-font)',
          boxShadow: 'none',
          borderRadius: 0,
          ...customStyles.container,
          ...(muiTablePaperProps.sx || {}),
        },
      }}
      muiTableContainerProps={{
        sx: {
          overflowX: 'auto',
          overflowY: 'visible',
          ...(muiTableContainerProps.sx || {}),
        },
        ...muiTableContainerProps,
      }}
      displayColumnDefOptions={{
        'mrt-row-expand': {
          muiTableHeadCellProps: { sx: { display: 'none' } },
          muiTableBodyCellProps: { sx: { display: 'none' } },
        },
      }}
      muiTableHeadProps={{
        sx: {
          opacity: 1,
        },
      }}
      muiTableHeadCellProps={{
        sx: {
          backgroundColor: 'var(--color-primary-shadow)',
          borderBottom: '1px solid var(--color-divider)',
          color: 'var(--color-font)',
          '& .MuiButtonBase-root': { opacity: 1, color: 'currentColor' },
          '& .MuiSvgIcon-root': { fill: 'currentColor' },
          padding: '8px',
          ...customStyles.header,
        },
      }}
      muiTableBodyCellProps={() => ({
        sx: {
          ...customStyles.cell,
          '&:last-of-type': {
            borderRight: 'none',
          },
        },
      })}
      muiTableBodyRowProps={({ row }) => {
        const disabled = !!row?.original?.disabled;
        const rowId = row?.original?.id;
        const hasSelectedRowId = selectedRowId != null && `${selectedRowId}`.length > 0;
        const isSelectedById =
          hasSelectedRowId && rowId != null && `${rowId}` === `${selectedRowId}`;
        const isSelectedByIds =
          Array.isArray(selectedRowIds) &&
          selectedRowIds.some((candidateId) => `${candidateId}` === `${rowId}`);

        return {
          onClick: () => {
            if (disabled) return;
            expandable && row.toggleExpanded();
            onRowClick && onRowClick(row.original);
          },
          hover: false,
          sx: {
            backgroundColor: 'var(--color-base)',
            color: 'var(--color-font)',
            ...(altRowSettings?.alternate && {
              backgroundColor: row.index % 2 ? altRowSettings?.color1 : altRowSettings?.color2,
            }),
            ...customStyles.row,
            ...((isSelectedById || isSelectedByIds) && {
              backgroundColor: selectedRowColor,
              color: selectedRowTextColor,
            }),
            ...(disabled && {
              cursor: 'not-allowed',
            }),
            ...(!disabled &&
              onRowClick && {
                cursor: 'pointer',
              }),
          },
        };
      }}
      muiDetailPanelProps={() => ({
        sx: {
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-primary-foreground)',
          ...customStyles.dropdown,
        },
      })}
      renderDetailPanel={({ row }) => (
        <div className="w-100" onClick={(e) => e.stopPropagation()}>
          {dropdownMenu(row.original)}
        </div>
      )}
      renderEmptyRowsFallback={() => (
        <div className="text-center text-theme p-4 fw-bold">
          {customEmptyMessage || 'No records to display'}
        </div>
      )}
    />
  );
};

export default ResponderBaseTable;
